import { Component, ReactNode } from 'react'
import { useTranslation } from '@spcc/core'
import { Button, AlertTriangle, RefreshCw, Home, Bug } from '@spcc/uikit'
import { analytics, extractComponentName } from '@spcc/domains/analytics'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** Changing this value will reset the boundary (useful for auth/nav transitions). */
  resetKey?: string | number
}

interface State {
  hasError: boolean
  error?: Error
  errorInfo?: React.ErrorInfo
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo })

    // Report to analytics (always tracked, regardless of consent)
    analytics.trackError({
      error,
      severity: 'critical',
      handled: true,
      componentName: extractComponentName(errorInfo.componentStack ?? undefined),
      context: {
        componentStack: errorInfo.componentStack?.substring(0, 1000),
        digest: (errorInfo as any).digest,
      },
    })

    // Also log to console in development
    if (import.meta.env.DEV) {
      console.error('Error caught by boundary:', error, errorInfo)
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!this.state.hasError) return
    if (prevProps.resetKey === this.props.resetKey) return

    this.setState({ hasError: false, error: undefined, errorInfo: undefined })
  }

  handleRefresh = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return <ErrorFallback
        error={this.state.error}
        onRefresh={this.handleRefresh}
        onGoHome={this.handleGoHome}
      />
    }

    return this.props.children
  }
}

interface ErrorFallbackProps {
  error?: Error
  onRefresh: () => void
  onGoHome: () => void
}

function ErrorFallback({ error, onRefresh, onGoHome }: ErrorFallbackProps) {
  const isDev = import.meta.env.DEV
  const { t } = useTranslation()

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background gradients */}
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-1/2 -left-1/2 w-full h-full bg-gradient-to-br from-destructive/20 via-transparent to-transparent rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -right-1/2 w-full h-full bg-gradient-to-tl from-accent/20 via-transparent to-transparent rounded-full blur-3xl" />
      </div>

      <div className="text-center px-4 max-w-lg">
        {/* Error Icon */}
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-destructive/10 mb-4">
            <AlertTriangle className="h-12 w-12 text-destructive" />
          </div>
        </div>

        <h1 className="text-3xl font-bold text-foreground mb-4">
          {t('errorBoundary.title')}
        </h1>
        <p className="text-lg text-muted-foreground mb-8">
          {t('errorBoundary.description')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <Button size="lg" onClick={onRefresh}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {t('errorBoundary.refreshPage')}
          </Button>
          <Button variant="outline" size="lg" onClick={onGoHome}>
            <Home className="h-4 w-4 mr-2" />
            {t('errorBoundary.goHome')}
          </Button>
        </div>

        {/* Error details for development */}
        {isDev && error && (
          <div className="glass rounded-xl p-4 text-left">
            <div className="flex items-center gap-2 text-destructive mb-2">
              <Bug className="h-4 w-4" />
              <span className="font-medium text-sm">{t('errorBoundary.developerInfo')}</span>
            </div>
            <p className="text-sm font-mono text-muted-foreground break-all">
              {error.name}: {error.message}
            </p>
            {error.stack && (
              <pre className="text-xs font-mono text-muted-foreground mt-2 overflow-x-auto max-h-40">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        <p className="text-sm text-muted-foreground mt-8">
          {t('errorBoundary.persistContact')}{' '}
          <a href="mailto:support@spcc.com" className="text-primary hover:underline">
            support@spcc.com
          </a>
        </p>
      </div>
    </div>
  )
}

export default ErrorBoundary
